import { Direction, Order } from 'app/constants/QueryParameter';
import { tFunctionType } from 'app/services/localization';
import { List, Map, Record } from 'immutable';

interface ISortOrder {
  id: string,
  order: Order,
  direction: Direction,
  getName: (t: tFunctionType) => string,
}

export class SortOrder extends Record<ISortOrder>(
  {
    id: '',
    order: Order.Id,
    direction: Direction.Ascending,
    getName: t => '',
  },
  'SortOrder',
) {
  // Force all parameters to be defined.
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(args: ISortOrder) {
    super(args);
  }
}

export const ID_ASCENDING = new SortOrder({
  id: 'id_asc',
  order: Order.Id,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.id-ascending'),
});

export const NAME_ASCENDING = new SortOrder({
  id: 'name_asc',
  order: Order.Name,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.name-ascending'),
});

export const NAME_DESCENDING = new SortOrder({
  id: 'name_desc',
  order: Order.Name,
  direction: Direction.Descending,
  getName: (t) => t('admin.editor.sort-order.name-descending'),
});

export const CREATED_AT_ASCENDING = new SortOrder({
  id: 'created_at_asc',
  order: Order.CreatedAt,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.created-at-ascending'),
});

export const CREATED_AT_DESCENDING = new SortOrder({
  id: 'created_at_desc',
  order: Order.CreatedAt,
  direction: Direction.Descending,
  getName: (t) => t('admin.editor.sort-order.created-at-descending'),
});

export const BOOKING_STARTS_AT_ASCENDING = new SortOrder({
  id: 'starts_at_asc',
  order: Order.StartsAt,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.starts-at-ascending'),
});

export const BOOKING_STARTS_AT_DESCENDING = new SortOrder({
  id: 'starts_at_desc',
  order: Order.StartsAt,
  direction: Direction.Descending,
  getName: (t) => t('admin.editor.sort-order.starts-at-descending'),
});

export const BOOKING_ENDS_AT_ASCENDING = new SortOrder({
  id: 'ends_at_asc',
  order: Order.EndsAt,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.ends-at-ascending'),
});

export const BOOKING_ENDS_AT_DESCENDING = new SortOrder({
  id: 'ends_at_desc',
  order: Order.EndsAt,
  direction: Direction.Descending,
  getName: (t) => t('admin.editor.sort-order.ends-at-descending'),
});

export const COURSE_POSITION_ASCENDING = new SortOrder({
  id: 'course_position_asc',
  order: Order.CoursePosition,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.course-position-ascending'),
});

export const CATEGORY_POSITION_ASCENDING = new SortOrder({
  id: 'category_position_asc',
  order: Order.CategoryPosition,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.category-position-ascending'),
});

export const CURRICULUM_POSITION_ASCENDING = new SortOrder({
  id: 'curriculum_position_asc',
  order: Order.CurriculumPosition,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.curriculum-position-ascending'),
});

export const FIRST_PUBLISHED_AT_ASCENDING = new SortOrder({
  id: 'first_published_at_asc',
  order: Order.FirstPublishedAt,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.first-published-at-ascending'),
});

export const RELEVANCE = new SortOrder({
  id: 'relevance',
  order: Order.Relevance,
  direction: Direction.Descending,
  getName: (t) => t('admin.editor.sort-order.relevance'),
});

export const FIRST_PUBLISHED_AT_DESCENDING = new SortOrder({
  id: 'first_published_at_desc',
  order: Order.FirstPublishedAt,
  direction: Direction.Descending,
  getName: (t) => t('admin.editor.sort-order.first-published-at-descending'),
});

export const LOWER_TARGET_LEVEL_ASCENDING = new SortOrder({
  id: 'target_level_range_min_asc',
  order: Order.TargetLevelRangeMin,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.lower-target-level-ascending'),
});

export const UPPER_TARGET_LEVEL_DESCENDING = new SortOrder({
  id: 'target_level_range_max_desc',
  order: Order.TargetLevelRangeMax,
  direction: Direction.Descending,
  getName: (t) => t('admin.editor.sort-order.upper-target-level-descending'),
});

export const EMAIL_ASCENDING = new SortOrder({
  id: 'email_asc',
  order: Order.Email,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.email-ascending'),
});

export const STAFF_EMAIL_ASCENDING = new SortOrder({
  id: 'staff_email_asc',
  order: Order.StaffEmail,
  direction: Direction.Ascending,
  getName: (t) => t('admin.editor.sort-order.email-ascending'),
});

export const EDITOR_CATEGORIES_ORDERS = List([
  CURRICULUM_POSITION_ASCENDING,
  NAME_ASCENDING,
  NAME_DESCENDING,
]);

export const EDITOR_COURSES_ORDERS = List([
  NAME_ASCENDING,
  NAME_DESCENDING,
  LOWER_TARGET_LEVEL_ASCENDING, // from easiest to hardest
  UPPER_TARGET_LEVEL_DESCENDING, // from hardest to easiest
]);

export const EDITOR_LESSONS_ORDERS = List([
  CREATED_AT_DESCENDING,
  CREATED_AT_ASCENDING,
  FIRST_PUBLISHED_AT_DESCENDING,
  FIRST_PUBLISHED_AT_ASCENDING,
]);

export const USER_ORDERS = List([
  CREATED_AT_DESCENDING,
  EMAIL_ASCENDING,
]);

export const LESSON_BOOKING_ORDERS = List([
  BOOKING_STARTS_AT_DESCENDING,
  BOOKING_STARTS_AT_ASCENDING,
  BOOKING_ENDS_AT_DESCENDING,
  BOOKING_ENDS_AT_ASCENDING,
  CREATED_AT_DESCENDING,
  CREATED_AT_ASCENDING,
]);

export const ALL_SORT_ORDERS_MAP = Map([
  ID_ASCENDING,
  RELEVANCE,
  NAME_ASCENDING,
  NAME_DESCENDING,
  CURRICULUM_POSITION_ASCENDING,
  CATEGORY_POSITION_ASCENDING,
  COURSE_POSITION_ASCENDING,
  FIRST_PUBLISHED_AT_DESCENDING,
  FIRST_PUBLISHED_AT_ASCENDING,
  CREATED_AT_DESCENDING,
  CREATED_AT_ASCENDING,
  LOWER_TARGET_LEVEL_ASCENDING,
  UPPER_TARGET_LEVEL_DESCENDING,
  EMAIL_ASCENDING,
  STAFF_EMAIL_ASCENDING,
  BOOKING_STARTS_AT_ASCENDING,
  BOOKING_STARTS_AT_DESCENDING,
  BOOKING_ENDS_AT_ASCENDING,
  BOOKING_ENDS_AT_DESCENDING,
].map(x => [x.id, x]));
